import React, { useRef, useState, useLayoutEffect } from 'react';
import StyledChord from './StyledChord';
import { AppMode } from '../types';

interface ChordCellProps {
  mode: AppMode;
  chord: string;
  measureIndex: number;
  chordIndex: number;
  isActive: boolean;
  isBeingPlayed?: boolean;
  error?: string | null;
  onClick: () => void;
  onPlayChord: (chord: string) => void;
  onChordHover: (chord: string, element: HTMLDivElement) => void;
  onChordLeave: () => void;
}

const ChordCell: React.FC<ChordCellProps> = ({ mode, chord, measureIndex, chordIndex, isActive, isBeingPlayed, error, onClick, onPlayChord, onChordHover, onChordLeave }) => {
  const [baseFontSize, setBaseFontSize] = useState(20);
  const containerRef = useRef<HTMLDivElement>(null);

  // Dynamic font sizing for EDITION mode only
  useLayoutEffect(() => {
    if (mode !== 'edition' || !containerRef.current || !chord) {
        setBaseFontSize(20); // Default size (like text-xl)
        return;
    }

    const containerElement = containerRef.current;
    
    // Create a temporary element to measure text width without affecting the DOM layout
    const tempEl = document.createElement('div');
    tempEl.style.fontFamily = 'monospace';
    tempEl.style.position = 'absolute';
    tempEl.style.visibility = 'hidden';
    tempEl.style.whiteSpace = 'nowrap';
    tempEl.style.display = 'flex';
    tempEl.style.alignItems = 'flex-start';
    tempEl.style.lineHeight = '1';

    document.body.appendChild(tempEl);

    // Function to set content and measure, mirroring the StyledChord component's structure
    const measureWidth = (size: number) => {
        const chordRegex = /^([A-G][#b♯♭]?)(.*)/;
        const match = chord.match(chordRegex);
        if (!match) {
            tempEl.innerHTML = `<span>${chord}</span>`;
            tempEl.style.fontSize = `${size}px`;
        } else {
            const [, root, extension] = match;
            const supSize = size * 0.7;
            const supTopOffset = size * -0.35;
            tempEl.style.fontSize = `${size}px`;
            tempEl.innerHTML = `
                <span>${root}</span>
                ${extension ? `<sup style="font-size: ${supSize}px; position: relative; top: ${supTopOffset}px; margin-left: 2px;">${extension}</sup>` : ''}
            `;
        }
        return tempEl.scrollWidth;
    };
    
    const MAX_FONT_SIZE = 36; // Increased from 32 for better readability
    const BASE_MIN_FONT_SIZE = 20; // Increased base minimum from 18
    const availableWidth = containerElement.clientWidth - 24; // Reduced padding assumption from 32 to 24

    // Analyze chord complexity to determine appropriate font size range
    const getChordComplexity = (chord: string) => {
      let complexity = 0;
      
      // Check for bass chord (adds visual complexity)
      if (chord.includes('/') && !chord.includes('6/9')) complexity += 2;
      
      // Check for accidentals
      if (chord.includes('#') || chord.includes('♯') || chord.includes('b') || chord.includes('♭')) complexity += 1;
      
      // Check for extensions and alterations
      if (chord.match(/\d/)) complexity += 1; // Numbers like 7, 9, 11, 13
      if (chord.includes('maj')) complexity += 1;
      if (chord.includes('add') || chord.includes('sus')) complexity += 1;
      if (chord.includes('dim') || chord.includes('aug')) complexity += 1;
      if (chord.match(/[♯#♭b][579]/)) complexity += 1; // Altered extensions
      
      return complexity;
    };

    const complexity = getChordComplexity(chord);
    
    // Calculate minimum font size based on subdivision (ensure readability in divided measures)
    const subdivisionCount = containerElement.parentElement?.children.length || 1;
    const subdivisionMinSize = subdivisionCount >= 4 ? BASE_MIN_FONT_SIZE + 2 : // 4 chords: min 22px
                              subdivisionCount >= 3 ? BASE_MIN_FONT_SIZE + 1 : // 3 chords: min 21px  
                              subdivisionCount >= 2 ? BASE_MIN_FONT_SIZE + 1 : // 2 chords: min 21px
                              BASE_MIN_FONT_SIZE; // 1 chord: min 20px
    
    // Adjust font size range based on complexity (less aggressive reduction)
    const adjustedMaxSize = Math.max(subdivisionMinSize + 4, MAX_FONT_SIZE - (complexity * 2)); // Reduced from * 3
    const adjustedMinSize = Math.max(subdivisionMinSize, adjustedMaxSize - 8); // Reduced from - 10

    // Iterative approach to find the best font size within the adjusted range
    let bestSize = adjustedMinSize;
    for (let size = adjustedMaxSize; size >= adjustedMinSize; size--) {
        if (measureWidth(size) <= availableWidth) {
            bestSize = size;
            break;
        }
    }
    
    document.body.removeChild(tempEl);
    setBaseFontSize(bestSize);

  }, [chord, mode]);

  let stateClasses = 'bg-[#1A1410]';
  if (isActive) {
    stateClasses = 'ring-2 ring-[#D4A853] bg-[#D4A853]/10 z-10';
  } else if (isBeingPlayed) {
    stateClasses = 'bg-[#362820]';
  }
  
  const errorClasses = error ? 'text-red-400' : 'text-[#D3CCBD]';

  const handlePlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!error) {
      onPlayChord(chord);
    }
  };

  const handleMouseEnter = () => {
    if (containerRef.current && chord && mode === 'edition') {
      onChordHover(chord, containerRef.current);
    }
  };

  const handleMouseLeave = () => {
    if (mode === 'edition') {
      onChordLeave();
    }
  };

  const renderContent = () => {
    if (chord === '') {
      return <span className="font-mono select-none text-[#D3CCBD] text-xl">/</span>
    }
    return <StyledChord chord={chord} baseFontSize={baseFontSize} className={errorClasses} />;
  }

  const hasBassChord = chord.includes('/') && !chord.includes('6/9');
  
  // Calculate dynamic bottom padding for bass chords based on font size
  const bassChordPadding = hasBassChord ? Math.max(16, baseFontSize * 0.5) : 0;
  
  return (
    <div
      ref={containerRef}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`relative w-full ${hasBassChord ? 'min-h-[4.5rem]' : 'min-h-[3.5rem]'} flex items-center justify-center hover:bg-[#362820] cursor-pointer transition-all duration-150 p-2 ${stateClasses}`}
      style={{ 
        paddingTop: mode === 'edition' && chord ? '1.5rem' : '0.5rem',
        paddingBottom: `${bassChordPadding}px`
      }}
      role="button"
      aria-pressed={isActive}
      title={error || ''}
      data-debug-id={`Level-3:ChordCell-${measureIndex + 1}-${chordIndex + 1}`}
    >
      {mode === 'edition' && chord && (
        <button
          onClick={handlePlayClick}
          className="absolute top-1 right-1 p-0.5 rounded-full text-gray-400 hover:bg-[#362820] hover:text-[#D4A853] transition-colors disabled:text-gray-600 disabled:hover:bg-transparent z-10"
          aria-label={`Play chord ${chord}`}
          disabled={!!error || chord === '%'}
          data-debug-id={`Level-3:PlayButton-${measureIndex + 1}-${chordIndex + 1}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
          </svg>
        </button>
      )}
      
      <div 
        className="flex items-center justify-center w-full"
        data-debug-id={`Level-3:ChordContent-${measureIndex + 1}-${chordIndex + 1}`}
      >
        {renderContent()}
      </div>
    </div>
  );
};

export default ChordCell;
